import React, { useEffect, useState } from 'react';
import { CalendarOff, Plus, Check, X, Trash2 } from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

// Plausible year window for a leave date. Mirrors the backend guard and stops
// typos like "0026-08-17" (a slipped keystroke on "2026-08-17").
const MIN_YEAR = 2025;
const MAX_YEAR = new Date().getFullYear() + 2;
const MIN_DATE = `${MIN_YEAR}-01-01`;
const MAX_DATE = `${MAX_YEAR}-12-31`;

const MANAGER_ROLES = ['admin', 'pm'];
const LEAD_ROLES = ['preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead'];

const prettyRole = (r) =>
  (r || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const statusBadge = (status) => {
  const map = {
    pending: 'bg-amber-100 text-amber-800',
    pending_admin: 'bg-orange-100 text-orange-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-700',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const statusLabel = (status) => ({
  pending: 'Pending TL/PM',
  pending_admin: 'Pending Admin',
  approved: 'Approved',
  rejected: 'Rejected',
}[status] || status);

const PAGE_SIZE = 15;

// Tabs follow the approval chain, so the work waiting on you is leftmost.
const STATUS_TABS = [
  { key: 'pending', label: 'Pending TL/PM' },
  { key: 'pending_admin', label: 'Pending Admin' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

const Leaves = () => {
  const { token, user } = useAuth();
  const role = user?.role;
  const isManager = MANAGER_ROLES.includes(role) || LEAD_ROLES.includes(role);

  const [tab, setTab] = useState(isManager ? 'manage' : 'mine');
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [manageableUsers, setManageableUsers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [balance, setBalance] = useState(null);

  // Team Leaves: one tab per status in the approval chain, each paged.
  // Managers land on the work waiting for them; a user sees their own full history.
  const [statusTab, setStatusTab] = useState(isManager ? 'pending' : 'all');
  const [statusCounts, setStatusCounts] = useState({});
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ page: 1, pages: 1, total: 0 });

  // Approval review: the overlap context for the leave being decided.
  const [review, setReview] = useState(null);       // { leave, decision }
  const [overlap, setOverlap] = useState(null);
  const [overlapLoading, setOverlapLoading] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [reviewNote, setReviewNote] = useState('');

  const blankForm = {
    user_id: '',
    leave_type: '',
    from_date: new Date().toISOString().slice(0, 10),
    to_date: new Date().toISOString().slice(0, 10),
    half: 'full', // full | AM | PM
    reason: '',
  };
  const [form, setForm] = useState(blankForm);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  // The API returns a bare array when no page_size is sent, and {items, counts,
  // pagination} when it is. Normalise both here so callers see one shape.
  const applyLeaveResponse = (data) => {
    if (Array.isArray(data)) {
      setLeaves(data);
      setStatusCounts({ all: data.length });
      setPageInfo({ page: 1, pages: 1, total: data.length });
      return;
    }
    setLeaves(data?.items || []);
    setStatusCounts(data?.counts || {});
    setPageInfo(data?.pagination || { page: 1, pages: 1, total: 0 });
  };

  // Both tabs are filtered and paged the same way — a personal history builds up
  // over a year too, and someone needs to see which stage their request is at.
  const leaveParams = (scope) => {
    const params = {
      scope: scope || (tab === 'mine' ? 'mine' : 'manage'),
      page,
      page_size: PAGE_SIZE,
    };
    if (statusTab !== 'all') params.status = statusTab;
    return params;
  };

  const loadLeaves = async (scope) => {
    const res = await api.get('/leaves', { ...authHeader, params: leaveParams(scope) });
    applyLeaveResponse(res.data);
  };

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = { headers: { Authorization: `Bearer ${token}` } };
      const reqs = [
        api.get('/admin/leave-types', headers),
        api.get('/leaves', { ...headers, params: leaveParams() }),
      ];
      if (isManager) reqs.push(api.get('/leaves/manageable-users', headers));
      const [typesRes, leavesRes, usersRes] = await Promise.all(reqs);
      setLeaveTypes(typesRes.data?.types || []);
      applyLeaveResponse(leavesRes.data);
      if (usersRes) setManageableUsers(usersRes.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load leaves');
    } finally {
      setLoading(false);
    }
  };

  const loadBalance = async (targetId) => {
    try {
      const params = {};
      if (targetId) params.user_id = targetId;
      const res = await api.get('/leaves/balance', { ...authHeader, params });
      setBalance(res.data && res.data.types && res.data.types.length ? res.data : null);
    } catch (e) {
      setBalance(null);
    }
  };

  useEffect(() => {
    if (token) loadAll();
    // eslint-disable-next-line
  }, [token, tab]);

  // Status tab or page change: reload just the list, not the whole page.
  useEffect(() => {
    if (token) loadLeaves();
    // eslint-disable-next-line
  }, [statusTab, page]);

  // Changing status tab, or switching between My/Team, restarts at page 1 —
  // otherwise page 3 of one list can open an empty page 3 of the other.
  useEffect(() => { setPage(1); }, [statusTab, tab]);

  // Refresh the balance card when the target user changes (or on tab switch).
  useEffect(() => {
    if (!token) return;
    const targetId = tab === 'manage' ? form.user_id : '';
    loadBalance(targetId);
    // eslint-disable-next-line
  }, [token, tab, form.user_id, leaves]);

  const submit = async () => {
    if (!form.from_date) { alert('Pick a start date.'); return; }
    // Catch typo'd years (e.g. 0026-08-17) before they reach the server — a bad
    // year turns a 3-day leave into a 2000-year one and blocks the user's clock-in.
    const badYear = [form.from_date, form.to_date]
      .filter(Boolean)
      .find((d) => {
        const y = Number(String(d).slice(0, 4));
        return !y || y < MIN_YEAR || y > MAX_YEAR;
      });
    if (badYear) {
      alert(`The date ${badYear} looks wrong — the year must be between ${MIN_YEAR} and ${MAX_YEAR}. Please check it.`);
      return;
    }
    if (form.half !== 'full' && form.from_date !== form.to_date) {
      alert('Half-day applies to a single day. Set the same From and To date.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        leave_type: form.leave_type || (leaveTypes[0] || ''),
        from_date: form.from_date,
        to_date: form.to_date || form.from_date,
        half_day: form.half === 'full' ? null : form.half,
        reason: form.reason,
      };
      // Managers may target another user; otherwise it's self.
      if (tab === 'manage' && form.user_id) payload.user_id = form.user_id;
      const res = await api.post('/leaves', payload, authHeader);
      setForm(blankForm);
      await loadLeaves();
      const st = res.data?.status;
      if (st === 'pending') {
        alert('Leave request submitted — awaiting TL/PM approval, then Admin.');
      } else if (st === 'pending_admin') {
        alert('Leave saved — awaiting Admin approval.');
      }
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to submit leave');
    } finally {
      setSaving(false);
    }
  };

  // Open the review panel instead of deciding straight away, so the approver
  // sees who else is already off before committing.
  const openReview = async (leave, decision) => {
    setReview({ leave, decision });
    setReviewNote('');
    setOverlap(null);
    setOverlapLoading(true);
    try {
      const res = await api.get(`/leaves/${leave.id}/overlap`, authHeader);
      setOverlap(res.data);
    } catch (e) {
      setOverlap({ error: e.response?.data?.error || 'Could not load who else is off' });
    } finally {
      setOverlapLoading(false);
    }
  };

  const submitDecision = async (decision) => {
    if (!review) return;
    setDeciding(true);
    try {
      await api.post(`/leaves/${review.leave.id}/decision`,
        { decision, note: reviewNote }, authHeader);
      setReview(null);
      await loadLeaves();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to update leave');
    } finally {
      setDeciding(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this leave?')) return;
    try {
      await api.delete(`/leaves/${id}`, authHeader);
      setLeaves((prev) => prev.filter((l) => l.id !== id));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to remove leave');
    }
  };

  // Server-side filtered and paged for the manage tab; My Leaves is unpaged.
  const visible = leaves;

  const fmtRange = (l) => {
    const same = l.from_date === l.to_date;
    const half = l.half_day ? ` (${l.half_day} half-day)` : '';
    const range = same ? `${l.from_date}${half}` : `${l.from_date} → ${l.to_date}`;
    if (!l.date_invalid) return range;
    return (
      <span>
        <span className="text-red-700 line-through">{range}</span>
        <span
          className="ml-2 inline-block px-1.5 py-0.5 rounded text-[11px] font-medium bg-red-100 text-red-700"
          title="These dates are not valid, so this leave is ignored. Delete it and re-enter with the correct dates."
        >
          Invalid dates
        </span>
      </span>
    );
  };

  if (loading) {
    return <div className="p-6"><div className="animate-pulse text-gray-500">Loading leaves…</div></div>;
  }

  return (
    <div className="p-6 overflow-x-hidden">
      <div className="flex items-center gap-2 mb-1">
        <CalendarOff className="w-6 h-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-800">Leaves</h1>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Request time off and manage your team's leaves.
      </p>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => { setTab('mine'); setStatusTab('all'); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'mine' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
        >
          My Leaves
        </button>
        {isManager && (
          <button
            onClick={() => { setTab('manage'); setStatusTab('pending'); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'manage' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          >
            Manage Team
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3 text-sm">{error}</div>
      )}

      {/* New leave / request */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-8 max-w-full">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Plus className="w-4 h-4" />
          {tab === 'manage'
            ? (role === 'admin' ? 'Add Leave (approved on save)' : 'Add Leave (awaits Admin approval)')
            : 'Request Leave'}
        </h2>

        {balance && (
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="text-xs font-medium text-gray-600 mb-2">
              {balance.category_name ? `${balance.category_name} — ` : ''}Balance for {balance.year}
              {tab === 'manage' && form.user_id ? '' : ' (you)'}
            </div>
            <div className="flex flex-wrap gap-2">
              {balance.types.map((b) => (
                <span
                  key={b.type}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs border ${b.remaining <= 0 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-white border-gray-200 text-gray-700'}`}
                  title={`Quota ${b.quota} · used ${b.used} · pending ${b.pending}`}
                >
                  <span className="font-medium">{b.type}:</span> {b.remaining}/{b.quota} left
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {tab === 'manage' && (
            <div>
              <label className="block text-xs text-gray-600 mb-1">User</label>
              <select
                value={form.user_id}
                onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
              >
                <option value="">Myself</option>
                {manageableUsers.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} ({prettyRole(u.role)})</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-600 mb-1">Leave type</label>
            <select
              value={form.leave_type}
              onChange={(e) => setForm((f) => ({ ...f, leave_type: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
            >
              {leaveTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">From</label>
              <input
                type="date"
                min={MIN_DATE}
                max={MAX_DATE}
                value={form.from_date}
                onChange={(e) => setForm((f) => ({ ...f, from_date: e.target.value, to_date: f.half !== 'full' ? e.target.value : (f.to_date < e.target.value ? e.target.value : f.to_date) }))}
                className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">To</label>
              <input
                type="date"
                min={form.from_date || MIN_DATE}
                max={MAX_DATE}
                value={form.to_date}
                disabled={form.half !== 'full'}
                onChange={(e) => setForm((f) => ({ ...f, to_date: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm disabled:bg-gray-100"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Duration</label>
            <select
              value={form.half}
              onChange={(e) => setForm((f) => {
                const half = e.target.value;
                return { ...f, half, to_date: half !== 'full' ? f.from_date : f.to_date };
              })}
              className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
            >
              <option value="full">Full day(s)</option>
              <option value="AM">Half day — Morning</option>
              <option value="PM">Half day — Afternoon</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Reason</label>
            <input
              type="text"
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              placeholder="Optional note"
              className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
            />
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            {saving ? 'Saving…' : tab === 'manage' ? 'Add Leave' : 'Submit Request'}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 max-w-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-800">
            {tab === 'manage' ? 'Team Leaves' : 'My Leaves'}
          </h2>
          {pageInfo.total > 0 && (
            <span className="text-sm text-gray-500">
              {pageInfo.total} record{pageInfo.total !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* One tab per status in the approval chain */}
        {(
          <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-4">
            {STATUS_TABS.map((t) => {
              const count = statusCounts[t.key] ?? (t.key === 'all' ? statusCounts.all : 0) ?? 0;
              const active = statusTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setStatusTab(t.key)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
                    active ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t.label}
                  {count > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                      active ? 'bg-blue-100 text-blue-700'
                        : t.key === 'pending' || t.key === 'pending_admin'
                          ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                    }`}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left">
                {tab === 'manage' && <th className="px-4 py-2 font-semibold text-gray-700">User</th>}
                <th className="px-4 py-2 font-semibold text-gray-700">Type</th>
                <th className="px-4 py-2 font-semibold text-gray-700">Dates</th>
                <th className="px-4 py-2 font-semibold text-gray-700">Reason</th>
                <th className="px-4 py-2 font-semibold text-gray-700">Status</th>
                <th className="px-4 py-2 font-semibold text-gray-700 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={tab === 'manage' ? 6 : 5} className="px-4 py-6 text-center text-gray-500">
                    No leaves to show.
                  </td>
                </tr>
              ) : (
                visible.map((l) => (
                  <tr key={l.id}>
                    {tab === 'manage' && (
                      <td className="px-4 py-2">
                        <div className="font-medium text-gray-800">{l.user_name}</div>
                        <div className="text-xs text-gray-500">{prettyRole(l.user_role)}</div>
                      </td>
                    )}
                    <td className="px-4 py-2 text-gray-700">{l.leave_type}</td>
                    <td className="px-4 py-2 text-gray-700">{fmtRange(l)}</td>
                    <td className="px-4 py-2 text-gray-600">{l.reason || '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${statusBadge(l.status)}`}>
                        {statusLabel(l.status)}
                      </span>
                      {l.stage1_approved_by_name && l.status === 'pending_admin' && (
                        <div className="text-xs text-gray-400 mt-0.5">TL/PM: {l.stage1_approved_by_name}</div>
                      )}
                      {l.decided_by_name && (l.status === 'approved' || l.status === 'rejected') && (
                        <div className="text-xs text-gray-400 mt-0.5">by {l.decided_by_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center whitespace-nowrap">
                      {/* Stage 1: TL/PM (or admin) first approval */}
                      {tab === 'manage' && l.can_manage && l.status === 'pending' && (
                        <>
                          <button
                            onClick={() => openReview(l, 'approved')}
                            className="text-green-700 hover:text-green-900 inline-flex items-center mr-2"
                            title={l.viewer_is_admin ? 'Approve (final)' : 'Approve → send to Admin'}
                          ><Check className="w-4 h-4" /></button>
                          <button
                            onClick={() => openReview(l, 'rejected')}
                            className="text-red-600 hover:text-red-800 inline-flex items-center mr-2"
                            title="Reject"
                          ><X className="w-4 h-4" /></button>
                        </>
                      )}
                      {/* Stage 2: only Admin gives final approval */}
                      {tab === 'manage' && l.viewer_is_admin && l.status === 'pending_admin' && (
                        <>
                          <button
                            onClick={() => openReview(l, 'approved')}
                            className="px-2 py-0.5 text-xs rounded bg-green-600 text-white hover:bg-green-700 mr-2"
                            title="Final approve"
                          >Final approve</button>
                          <button
                            onClick={() => openReview(l, 'rejected')}
                            className="text-red-600 hover:text-red-800 inline-flex items-center mr-2"
                            title="Reject"
                          ><X className="w-4 h-4" /></button>
                        </>
                      )}
                      {((tab === 'mine' && (l.status === 'pending' || l.status === 'pending_admin')) || (tab === 'manage' && l.can_manage)) && (
                        <button
                          onClick={() => remove(l.id)}
                          className="text-gray-500 hover:text-red-700 inline-flex items-center"
                          title="Remove"
                        ><Trash2 className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pageInfo.pages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-gray-500">
              Page {pageInfo.page} of {pageInfo.pages}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pageInfo.page <= 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
              >Previous</button>
              <button
                onClick={() => setPage((p) => Math.min(pageInfo.pages, p + 1))}
                disabled={pageInfo.page >= pageInfo.pages}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Approval review ─────────────────────────────────────────
          Shows who else is already off before the decision is made. */}
      {review && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800">Review leave request</h3>
              <button onClick={() => setReview(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto">
              {/* The request */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-base font-semibold text-gray-900">{review.leave.user_name}</span>
                  <span className="text-sm text-gray-500">{prettyRole(review.leave.user_role)}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(review.leave.status)}`}>
                    {statusLabel(review.leave.status)}
                  </span>
                </div>
                <div className="mt-2 text-sm text-gray-700">
                  <span className="font-medium">{review.leave.leave_type}</span>
                  {' · '}{fmtRange(review.leave)}
                  {overlap?.leave?.days ? ` · ${overlap.leave.days} day(s)` : ''}
                </div>
                {review.leave.reason && (
                  <p className="mt-2 text-sm text-gray-600 italic">“{review.leave.reason}”</p>
                )}
                {overlap?.balance?.types?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {overlap.balance.types.map((t) => (
                      <span key={t.type} className="px-2 py-1 rounded-md bg-white border border-gray-200 text-xs text-gray-700">
                        {t.type}: <span className="font-semibold">{t.remaining}</span> left of {t.quota}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {overlapLoading ? (
                <p className="text-sm text-gray-500 py-4 text-center">Checking who else is off…</p>
              ) : overlap?.error ? (
                <p className="text-sm text-amber-700">{overlap.error}</p>
              ) : overlap ? (
                <>
                  {/* Cover warning */}
                  {overlap.peak?.count > 0 && (
                    <div className={`rounded-lg px-4 py-3 text-sm border ${
                      overlap.team_size && overlap.peak.count >= Math.max(2, Math.ceil(overlap.team_size / 2))
                        ? 'bg-red-50 border-red-200 text-red-800'
                        : 'bg-amber-50 border-amber-200 text-amber-800'
                    }`}>
                      <span className="font-semibold">
                        {overlap.peak.count} other{overlap.peak.count !== 1 ? 's' : ''} from {overlap.leave.team || 'this team'} already
                        off on {overlap.peak.date}
                      </span>
                      {overlap.team_size ? ` — team of ${overlap.team_size}.` : '.'}
                      {' '}Approving makes it {overlap.peak.count + 1}.
                    </div>
                  )}

                  <div>
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">
                      Same team, overlapping these dates ({overlap.same_team.length})
                    </h4>
                    {overlap.same_team.length === 0 ? (
                      <p className="text-sm text-gray-500">Nobody else from this team is off.</p>
                    ) : (
                      <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                        {overlap.same_team.map((o, i) => (
                          <div key={i} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                            <span className="font-medium text-gray-800">{o.user_name}</span>
                            <span className="text-gray-600">
                              {o.from_date === o.to_date ? o.from_date : `${o.from_date} → ${o.to_date}`}
                              {o.half_day ? ` (${o.half_day} half-day)` : ''}
                            </span>
                            <span className="text-gray-500">{o.leave_type}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(o.status)}`}>
                              {statusLabel(o.status)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {overlap.per_day?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">Day by day</h4>
                      <div className="flex flex-wrap gap-2">
                        {overlap.per_day.map((d) => (
                          <span key={d.date} className={`px-2 py-1 rounded-md text-xs border ${
                            d.count >= 3 ? 'bg-red-50 border-red-200 text-red-800'
                              : d.count === 2 ? 'bg-amber-50 border-amber-200 text-amber-800'
                                : 'bg-gray-50 border-gray-200 text-gray-700'
                          }`}>
                            {d.date}: {d.count} off
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Counts same-team full-day absences. Half-days are excluded.
                      </p>
                    </div>
                  )}

                  {overlap.other_teams_count > 0 && (
                    <p className="text-sm text-gray-500">
                      {overlap.other_teams_count} person(s) from other teams are also off on these dates.
                    </p>
                  )}
                </>
              ) : null}

              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Note {review.decision === 'rejected' ? '(reason for rejection)' : '(optional)'}
                </label>
                <input
                  type="text"
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  placeholder={review.decision === 'rejected' ? 'Why is this being rejected?' : 'Optional note'}
                  className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setReview(null)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100"
              >Cancel</button>
              <button
                onClick={() => submitDecision('rejected')}
                disabled={deciding}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >Reject</button>
              <button
                onClick={() => submitDecision('approved')}
                disabled={deciding}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {review.leave.viewer_is_admin && review.leave.status === 'pending_admin'
                  ? 'Final approve' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaves;
